import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import useOfflineQueue from '../../hooks/useOfflineQueue.js';

function money(n) {
  if (n == null) return '—';
  return `PHP ${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

const SELECTION_OPTIONS = [
  { value: 'Assigned', label: 'Direct Assigned Pilot' },
  { value: 'OpenMarket', label: 'Publish to Open Pilot Marketplace' },
  { value: 'NoPilot', label: 'No Pilot Required' },
];

export default function PilotCoordinatorPage() {
  const [bookings, setBookings] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [myPilot, setMyPilot] = useState(null);
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);

  const [bookingId, setBookingId] = useState('');
  const [selectionType, setSelectionType] = useState('Assigned');
  const [pilotId, setPilotId] = useState('');
  const [pilotageFee, setPilotageFee] = useState('');

  // Offline-first accept for the pilot PWA (NFR-2): an accepted request is queued
  // and replayed on reconnect, so a pilot on a weak connection never loses it.
  const sendAccept = useCallback((item) => api.acceptPilotRequest(item.requestId), []);
  const { syncStatus, enqueueOrSend } = useOfflineQueue('pilot_pwa_pending', sendAccept);

  const load = useCallback(async () => {
    const [bk, pl, mk, me, mine] = await Promise.all([
      api.listPilotBookings(),
      api.listAccreditedPilots(),
      api.getPilotMarketplace(),
      api.getMyPilotProfile().catch(() => null),
      api.getMyPilotAssignments().catch(() => []),
    ]);
    setBookings(bk || []);
    setPilots(pl || []);
    setMarketplace(mk || []);
    setMyPilot(me || null);
    setMyAssignments(mine || []);
    setBookingId((prev) => prev || (bk && bk[0] ? bk[0].id : ''));
    setPilotId((prev) => prev || (pl && pl[0] ? pl[0].id : ''));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load pilot coordination data.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const process = async () => {
    if (!bookingId) {
      setPageMsg({ type: 'danger', text: 'Select an active booking first.' });
      return;
    }
    setSubmitting(true);
    setPageMsg(null);
    try {
      const payload = { bookingId, selectionType };
      if (selectionType === 'Assigned') payload.pilotId = pilotId;
      if (pilotageFee !== '' && selectionType !== 'NoPilot') payload.pilotageFee = Number(pilotageFee);
      const res = await api.assignPilotage(payload);
      await load();
      const verb =
        res.status === 'Published'
          ? `published to the open pilot marketplace`
          : res.status === 'Assigned'
            ? `assigned to ${res.pilotName}`
            : `recorded as no-pilot`;
      setPageMsg({
        type: 'success',
        text: `Pilotage for ${res.bookingRef} ${verb}.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to process pilotage assignment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const accept = async (id) => {
    setAcceptingId(id);
    setPageMsg(null);
    // Optimistic update so the action holds even while offline (queued for sync).
    const row = marketplace.find((m) => m.id === id);
    setMarketplace((list) =>
      list.map((m) => (m.id === id ? { ...m, status: 'Accepted', acceptedByName: myPilot?.name } : m))
    );
    try {
      await enqueueOrSend({ requestId: id });
      if (navigator.onLine) await load();
      setPageMsg({
        type: 'success',
        text: navigator.onLine
          ? `You accepted the pilotage request for ${row?.bookingRef || ''}.`
          : `Acceptance for ${row?.bookingRef || ''} queued — will sync when back online.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to accept request.' });
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="pilot-coordinator-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-user-tie me-2" aria-hidden="true" />
                Pilotage Assignments &amp; Market Coordinator
              </h3>
              <p className="text-muted mb-0 small">
                Coordinate harbor pilotage assignments or publish open pilot requests to the
                independent pilot association portal.
              </p>
            </div>
            <span className="badge bg-info text-dark p-2">Marketplace Bidding: Active</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="pilot-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="pilot-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: assign / publish form */}
          <div className="col-lg-5 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-handshake me-2" aria-hidden="true" />
                  Coordinate Pilot Assignment
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="pBooking" className="form-label fw-bold small text-muted">
                    Select Active Booking <span className="text-danger">*</span>
                  </label>
                  <select
                    id="pBooking"
                    className="form-select"
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value)}
                    data-testid="booking-select"
                  >
                    {bookings.length === 0 && <option value="">No active bookings</option>}
                    {bookings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bookingRef} ({b.vesselName || 'Vessel'} — {b.portName || 'Port'})
                        {b.pilotageStatus ? ` · ${b.pilotageStatus}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="pilotSelectionType" className="form-label fw-bold small text-muted">
                    Selection Mode <span className="text-danger">*</span>
                  </label>
                  <select
                    id="pilotSelectionType"
                    className="form-select"
                    value={selectionType}
                    onChange={(e) => setSelectionType(e.target.value)}
                    data-testid="selection-type"
                  >
                    {SELECTION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectionType === 'Assigned' && (
                  <div className="mb-3" data-testid="direct-pilot-group">
                    <label htmlFor="directPilot" className="form-label fw-bold small text-muted">
                      Designated Accredited Pilot <span className="text-danger">*</span>
                    </label>
                    <select
                      id="directPilot"
                      className="form-select"
                      value={pilotId}
                      onChange={(e) => setPilotId(e.target.value)}
                      data-testid="pilot-select"
                    >
                      {pilots.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.district})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectionType !== 'NoPilot' && (
                  <div className="mb-3">
                    <label htmlFor="pilotageFee" className="form-label fw-bold small text-muted">
                      Pilotage Fee {selectionType === 'OpenMarket' ? '(offered to market)' : '(optional)'}
                    </label>
                    <input
                      id="pilotageFee"
                      type="number"
                      min="0"
                      step="100"
                      className="form-control"
                      value={pilotageFee}
                      onChange={(e) => setPilotageFee(e.target.value)}
                      placeholder="e.g. 15000"
                      data-testid="pilotage-fee"
                    />
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary w-100 mt-2"
                  onClick={process}
                  disabled={submitting || !bookingId}
                  data-testid="process-pilotage"
                >
                  {submitting && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  <i className="fas fa-check-circle me-1" aria-hidden="true" />
                  Process Pilotage Assignment
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: open marketplace */}
          <div className="col-lg-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-globe me-2" aria-hidden="true" />
                  Open Pilotage Marketplace
                </h5>
                {myPilot && (
                  <span className="d-flex align-items-center gap-2">
                    <span
                      className={`badge ${syncStatus === 'Offline' || syncStatus === 'Offline — queued' ? 'bg-warning text-dark' : 'bg-light text-secondary border'}`}
                      data-testid="pilot-sync-status"
                    >
                      <i
                        className={`fas ${syncStatus === 'Offline' || syncStatus === 'Offline — queued' ? 'fa-wifi-slash text-danger' : 'fa-wifi text-success'} me-1`}
                        aria-hidden="true"
                      />
                      {syncStatus}
                    </span>
                    <span className="badge bg-success" data-testid="pilot-identity">
                      Signed in as {myPilot.name}
                    </span>
                  </span>
                )}
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Offer</th>
                        <th>Vessel</th>
                        <th>Scheduled</th>
                        <th>Fee</th>
                        <th className="text-center">Market Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplace.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No open pilotage requests.
                          </td>
                        </tr>
                      )}
                      {marketplace.map((m) => (
                        <tr key={m.id} data-testid="market-row" data-ref={m.bookingRef}>
                          <td className="fw-bold">{m.bookingRef}</td>
                          <td className="small">
                            {m.vesselName || '—'}
                            <span className="d-block text-muted">{m.portName || '—'}</span>
                          </td>
                          <td className="small">{fmt(m.scheduledAt)}</td>
                          <td className="fw-bold text-primary">{money(m.pilotageFee)}</td>
                          <td className="text-center">
                            {m.status === 'Accepted' ? (
                              <span className="badge bg-secondary" data-testid="market-accepted">
                                <i className="fas fa-check me-1" aria-hidden="true" />
                                Accepted{m.acceptedByName ? ` · ${m.acceptedByName}` : ''}
                              </span>
                            ) : myPilot ? (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => accept(m.id)}
                                disabled={acceptingId === m.id}
                                data-testid="accept-request"
                              >
                                {acceptingId === m.id && (
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                )}
                                Accept Request
                              </button>
                            ) : (
                              <span className="badge bg-info text-dark" data-testid="market-open">
                                Open for bidding
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Pilot PWA: my active assignments tracker */}
          {myPilot && (
            <div className="col-12 mb-4" data-testid="my-assignments-section">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white pt-4 border-bottom-0">
                  <h5 className="mb-0 text-secondary">
                    <i className="fas fa-clipboard-list me-2" aria-hidden="true" />
                    My Active Pilotage Assignments
                  </h5>
                  <p className="text-muted small mb-0">
                    Jobs assigned to or accepted by you — vessel specs, berth, planned time, and
                    assigned tugs.
                  </p>
                </div>
                <div className="card-body">
                  {myAssignments.length === 0 ? (
                    <div className="text-center text-muted py-4" data-testid="no-assignments">
                      <i className="fas fa-anchor fa-2x mb-2 text-secondary" aria-hidden="true" />
                      <p className="mb-0 small">No active assignments yet. Accept an open request above.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {myAssignments.map((a) => (
                        <div className="col-md-6 col-xl-4 mb-3" key={a.id}>
                          <div
                            className="border rounded p-3 h-100 bg-light"
                            data-testid="assignment-card"
                            data-ref={a.bookingRef}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="fw-bold mb-0">{a.bookingRef}</h6>
                              <span className="badge bg-success" data-testid="assignment-status">
                                {a.status}
                              </span>
                            </div>
                            <p className="mb-1 small">
                              <i className="fas fa-ship me-1 text-primary" aria-hidden="true" />
                              <strong data-testid="assignment-vessel">{a.vessel.name || '—'}</strong>
                              {a.vessel.grossTonnage != null && (
                                <span className="text-muted"> · {a.vessel.grossTonnage} GT</span>
                              )}
                              {a.vessel.loaM != null && (
                                <span className="text-muted"> · LOA {a.vessel.loaM} m</span>
                              )}
                            </p>
                            <p className="mb-1 small">
                              <i className="fas fa-map-marker-alt me-1 text-danger" aria-hidden="true" />
                              {a.movementType} — {a.portName || '—'}
                              {a.berthName ? ` / ${a.berthName}` : ''}
                            </p>
                            <p className="mb-2 small">
                              <i className="fas fa-clock me-1 text-secondary" aria-hidden="true" />
                              {fmt(a.scheduledAt)}
                            </p>
                            <div className="small">
                              <span className="text-muted d-block mb-1">Assigned Tugs:</span>
                              {a.assignedTugs.length === 0 ? (
                                <span className="text-muted fst-italic" data-testid="assignment-tugs">
                                  None assigned yet
                                </span>
                              ) : (
                                <span data-testid="assignment-tugs">
                                  {a.assignedTugs.map((t) => (
                                    <span key={t.id} className="badge bg-primary me-1 mb-1">
                                      {t.name} ({t.bollardPullTons}T)
                                    </span>
                                  ))}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
